import sandboxConfig from "./sandbox-config.json";

export const SANDBOX_AUTH_COOKIE = sandboxConfig.cookieName;
export const SANDBOX_ORG_COOKIE = "tcos_sandbox_org";
export const SANDBOX_BRAND_COOKIE = sandboxConfig.brandCookieName;
export const SANDBOX_ORG_ID = "org_tcos_sandbox";
export const SANDBOX_NORDIC_VISITOR_COMPANY_ID = "company_tcos_sandbox_nordic_visitor";
export const SANDBOX_ICELAND_TRAVEL_COMPANY_ID = "company_tcos_sandbox_iceland_travel";

export const SANDBOX_ORGANIZATIONS = [
  { id: SANDBOX_ORG_ID, label: "TCOS Sandbox" },
  { id: "org_tcos_sandbox_north", label: "Northwind Iceland" },
  { id: "org_tcos_sandbox_south", label: "South Coast Journeys" },
] as const;

const SANDBOX_ORG_IDS = new Set<string>(
  SANDBOX_ORGANIZATIONS.map((organization) => organization.id)
);

export function isAllowedSandboxOrgId(value: string | undefined | null): value is string {
  return Boolean(value && SANDBOX_ORG_IDS.has(value));
}

export function parseSandboxOrgId(value: string | undefined | null): string | null {
  return isAllowedSandboxOrgId(value) ? value : null;
}

export function normalizeSandboxOrgId(value: string | undefined | null): string {
  return parseSandboxOrgId(value) ?? SANDBOX_ORG_ID;
}

export type SandboxRole = "admin" | "catalog" | "planner" | "viewer";
export const DEFAULT_SANDBOX_ROLE: SandboxRole = "admin";
export type SandboxBrand = "travel-connect" | "nordic-visitor" | "iceland-travel";

export interface SandboxUser {
  role: SandboxRole;
  userId: string;
  name: string;
  email: string;
  permissions: string[];
}

export const SANDBOX_BRANDS: Record<
  SandboxBrand,
  { companyId: string | null; name: string; orgId: string }
> = {
  "travel-connect": {
    companyId: null,
    name: "Travel Connect",
    orgId: SANDBOX_ORG_ID,
  },
  "nordic-visitor": {
    companyId: SANDBOX_NORDIC_VISITOR_COMPANY_ID,
    name: "Nordic Visitor",
    orgId: SANDBOX_ORG_ID,
  },
  "iceland-travel": {
    companyId: SANDBOX_ICELAND_TRAVEL_COMPANY_ID,
    name: "Iceland Travel",
    orgId: SANDBOX_ORG_ID,
  },
};

const READ_PERMISSIONS = [
  "org:agents:read",
  "org:catalog:read",
  "org:cruise:read",
  "org:integrations:read",
  "org:packages:read",
  "org:pricing:read",
  "org:sales:read",
  "org:audit:read",
  "org:notifications:read",
  "org:onboarding:read",
] as const;

const WRITE_PERMISSIONS = [
  "org:brands:admin",
  "org:finance:manage_tax",
  "org:brands:admin",
  "org:catalog:write",
  "org:cruise:write",
  "org:editorial:approve",
  "org:editorial:write",
  "org:integrations:write",
  "org:packages:write",
  "org:packages:publish",
  "org:sales:write",
  "org:agents:write",
  "org:agents:approve",
  "org:pricing:write",
  "org:sources:admin",
  "org:onboarding:manage",
] as const;

export const SANDBOX_USERS: Record<SandboxRole, SandboxUser> = {
  admin: {
    role: "admin",
    userId: "sandbox_user_admin",
    name: "Sandbox Admin",
    email: "admin@sandbox.tcos.local",
    permissions: [...READ_PERMISSIONS, ...WRITE_PERMISSIONS],
  },
  catalog: {
    role: "catalog",
    userId: "sandbox_user_catalog",
    name: "Sandbox Catalog Operator",
    email: "catalog@sandbox.tcos.local",
    permissions: [
      "org:brands:admin",
      "org:catalog:read",
      "org:catalog:write",
      "org:integrations:read",
    ],
  },
  planner: {
    role: "planner",
    userId: "sandbox_user_planner",
    name: "Sandbox Package Planner",
    email: "planner@sandbox.tcos.local",
    permissions: ["org:catalog:read", "org:packages:read", "org:packages:write"],
  },
  viewer: {
    role: "viewer",
    userId: "sandbox_user_viewer",
    name: "Sandbox Viewer",
    email: "viewer@sandbox.tcos.local",
    permissions: [...READ_PERMISSIONS],
  },
};

export function isSandboxAuthEnabled(): boolean {
  return (
    process.env.NODE_ENV !== "production" &&
    process.env.TCOS_SANDBOX === "1" &&
    process.env.TCOS_SANDBOX_AUTH === "1"
  );
}

export function isPublicSandboxAuthEnabled(): boolean {
  return (
    process.env.NODE_ENV !== "production" &&
    process.env.NEXT_PUBLIC_TCOS_SANDBOX === "1" &&
    process.env.NEXT_PUBLIC_TCOS_SANDBOX_AUTH === "1"
  );
}

export function normalizeSandboxRole(value: string | undefined | null): SandboxRole {
  if (value && Object.hasOwn(SANDBOX_USERS, value)) return value as SandboxRole;
  return value ? "viewer" : DEFAULT_SANDBOX_ROLE;
}

export function getSandboxUser(role: string | undefined | null): SandboxUser {
  return SANDBOX_USERS[normalizeSandboxRole(role)];
}

export function normalizeSandboxBrand(value: string | undefined | null): SandboxBrand {
  return value && Object.hasOwn(SANDBOX_BRANDS, value) ? (value as SandboxBrand) : "nordic-visitor";
}

export function sandboxOrgIdForBrand(value: string | undefined | null): string {
  return SANDBOX_BRANDS[normalizeSandboxBrand(value)].orgId;
}

export function sandboxCompanyIdForBrand(value: string | undefined | null): string | null {
  return SANDBOX_BRANDS[normalizeSandboxBrand(value)].companyId;
}

export function sandboxBrandForCompanyId(
  companyId: string | null | undefined
): SandboxBrand | null {
  if (!companyId) return null;
  for (const brand of Object.keys(SANDBOX_BRANDS) as SandboxBrand[]) {
    if (SANDBOX_BRANDS[brand].companyId === companyId) return brand;
  }
  return null;
}

export function sandboxCompanyChoices(): Array<{
  brand: SandboxBrand;
  companyId: string;
  name: string;
}> {
  return (Object.keys(SANDBOX_BRANDS) as SandboxBrand[]).flatMap((brand) => {
    const companyId = SANDBOX_BRANDS[brand].companyId;
    return companyId ? [{ brand, companyId, name: SANDBOX_BRANDS[brand].name }] : [];
  });
}
